import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { concatLatestFrom } from '@ngrx/operators';
import { BehaviorSubject, combineLatest, concatMap, EMPTY, Observable, of, scan } from 'rxjs';

import { TableInfo } from '@blaze-shared/api-contracts-domain';
import { ConnectorConfiguration } from '@blaze-shared/connectors-domain';
import {
  CopyConnectorConfigEnvButtonComponent,
  DialogModule,
  EnvironmentIndicatorComponent,
  SelectComponent,
} from '@blaze-shared/shared-feature';
import {
  ButtonBarComponent,
  ButtonComponent,
  ElementLoadingIndicatorComponent,
  PanelComponent,
  PanelRef,
} from '@blaze-shared/shared-ui';
import { SelectOption } from '@blaze-shared/shared-util';
import { IconsModule } from '@blaze-shared/theming-data-access';

import { BaseConnectorDialogComponent } from '../../__base-connector-dialog/base-connector-dialog.component';
import { MappingRowComponent } from '../../mapping-row/mapping-row.component';
import { MappingOption } from '../../models/data-connector-dialog';
import { prepareConnectorConfiguration, updateFormArrayControlsLengthMutably } from '../../utils';

import {
  baseFieldOptions,
  CalendlyConfiguration,
  CalendlyField,
  CalendlyMapping,
  compatibleMappings,
  ConnectorForm,
  FormMapping,
  LoadingState,
} from './calendly-connector-dialog.models';

@UntilDestroy()
@Component({
  selector: 'blaze-calendly-connector-dialog',
  templateUrl: './calendly-connector-dialog.component.html',
  styleUrls: ['./calendly-connector-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogModule,
    EnvironmentIndicatorComponent,
    CopyConnectorConfigEnvButtonComponent,
    ButtonBarComponent,
    AsyncPipe,
    ReactiveFormsModule,
    SelectComponent,
    ElementLoadingIndicatorComponent,
    MappingRowComponent,
    ButtonComponent,
    IconsModule,
    PanelComponent,
  ],
  host: { class: 'data-connector-dialog' },
})
export class CalendlyConnectorDialogComponent
  extends BaseConnectorDialogComponent<CalendlyConfiguration>
  implements OnInit
{
  private panelRef = inject(PanelRef);

  private availableMappings$$ = new BehaviorSubject<MappingOption<CalendlyField>[]>([]);
  private loading$$ = new BehaviorSubject<LoadingState | undefined>(undefined);

  serviceName = 'calendly';
  connectorForm: FormGroup<ConnectorForm>;

  availableMappings$ = this.availableMappings$$.asObservable();
  loading$: Observable<LoadingState | undefined>;

  get canRemoveMapping(): boolean {
    return this.connectorForm.controls.mappings.length > 1;
  }

  get isTableSelectDisabled(): boolean {
    return !this.connectorForm.value.projectId;
  }

  constructor() {
    super();

    this.connectorForm = new FormGroup<ConnectorForm>({
      connectorName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      personalToken: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      projectId: new FormControl(null, { validators: [Validators.required] }),
      tableId: new FormControl(null, { validators: [Validators.required] }),
      mappings: new FormArray<FormGroup<FormMapping>>([this.createMappingControls()]),
    });

    const projectId$ = this.connectorForm.controls.projectId.valueChanges;
    const tableId$ = this.connectorForm.controls.tableId.valueChanges;

    projectId$
      .pipe(
        concatMap((projectId) => {
          this.connectorForm.patchValue({ tableId: null });
          this.loading$$.next({ tables: true });
          if (!projectId) {
            return EMPTY;
          }

          return this.loadTables(projectId);
        }),
        untilDestroyed(this),
      )
      .subscribe((tables) => {
        this.availableTables$$.next(tables.filter((table) => !table.systemTableType));
        this.loading$$.next({ tables: false });
      });

    tableId$
      .pipe(
        untilDestroyed(this),
        concatLatestFrom(() => this.availableTables$$),
      )
      .subscribe(([tableId, tables]) => {
        const table = tables.find((table) => table.id === tableId);
        this.availableMappings$$.next(this.determineAvailableMappings(table));
      });

    this.loading$ = this.loading$$.pipe(
      scan(
        (acc, curr) => {
          return {
            ...acc,
            projects: curr?.projects || false,
            tables: curr?.tables || false,
          };
        },
        { projects: false, tables: false, connector: false } as LoadingState,
      ),
    );
  }

  ngOnInit(): void {
    this.goToForm();
    this.fillForm(this.getConfiguration(this.connector));
  }

  getConfiguration(connector?: ConnectorConfiguration): CalendlyConfiguration {
    const connectorId = connector?.id ?? '';
    const connectorName = connector?.name ?? '';
    const configuration = connector?.configuration ?? {};

    const projectId = String(configuration?.projectId ?? '');
    const tableId = projectId ? String(configuration?.tableId ?? '') : '';
    const personalToken = String(configuration?.personalToken ?? '');

    let mappings: CalendlyMapping[] = [];
    if (tableId) {
      mappings = configuration?.mappings
        ? (configuration?.mappings as unknown as CalendlyMapping[])
        : [];
    }

    return {
      connectorId,
      connectorName,
      mappings,
      personalToken,
      projectId,
      tableId,
    };
  }

  fillForm(configuration: CalendlyConfiguration): void {
    const { connectorId, connectorName, projectId, tableId, personalToken } = configuration;
    this.loadingDialog$$.next(true);

    if (!connectorId) {
      this.loadProjects().subscribe((projects) => {
        this.availableProjects$$.next(projects);
        this.loadingDialog$$.next(false);
      });
      return;
    }
    this.connectorId = connectorId;

    combineLatest({
      projects: this.loadProjects(),
      tables: projectId ? this.loadTables(projectId) : of([]),
    }).subscribe(({ projects, tables }) => {
      this.availableProjects$$.next(projects);
      this.availableTables$$.next(tables);

      const selectedTable = tables.find((table) => table.id === tableId);
      this.availableMappings$$.next(this.determineAvailableMappings(selectedTable));

      const mappings = configuration.mappings.map(({ sourceName, columnId }) => ({
        fieldId: sourceName,
        columnId,
      }));
      updateFormArrayControlsLengthMutably(
        this.connectorForm.controls.mappings,
        mappings.length,
        () => this.createMappingControls(),
      );

      this.connectorForm.patchValue(
        {
          connectorName,
          personalToken,
          projectId,
          tableId,
          mappings,
        },
        { emitEvent: false },
      );

      this.loadingDialog$$.next(false);
    });
  }

  determineAvailableMappings(table: TableInfo | undefined): MappingOption<CalendlyField>[] {
    if (!table) {
      return [];
    }

    return baseFieldOptions.map((option) => {
      const field = option.value;

      const columns: SelectOption<string>[] = table.columns
        .filter(
          (col) =>
            !col.isInternal &&
            !col.isComputed &&
            col.dataType &&
            compatibleMappings[field].includes(col.dataType),
        )
        .map((col) => ({ label: col.name, value: col.id ?? '' }))
        .filter((option) => !!option.value);

      return { ...option, columns };
    });
  }

  createMappingControls(
    fieldId: null | CalendlyField = null,
    columnId: null | string = null,
  ): FormGroup<FormMapping> {
    return new FormGroup<FormMapping>({
      fieldId: new FormControl(fieldId, { validators: [Validators.required] }),
      columnId: new FormControl(columnId, { validators: [Validators.required] }),
    });
  }

  addMapping(): void {
    this.connectorForm.controls.mappings.push(this.createMappingControls());
  }

  removeMapping(index: number): void {
    this.connectorForm.controls.mappings.removeAt(index);
  }

  saveConnector(): void {
    if (this.connectorForm.invalid) {
      return;
    }

    const { connectorName, personalToken, projectId, tableId, mappings } = this.connectorForm.value;

    if (!connectorName || !personalToken || !projectId || !tableId) {
      return;
    }

    const mappingsParsed = (mappings ?? [])
      .filter((mapping) => mapping.fieldId && mapping.columnId)
      .map((mapping) => ({
        sourceName: mapping.fieldId,
        columnId: mapping.columnId,
      }));

    const connectorConfig = prepareConnectorConfiguration(
      connectorName,
      this.template?.id,
      this.accountId,
      this.connector,
      {
        personalToken,
        projectId,
        tableId,
        mappings: mappingsParsed as any,
      },
    );

    this.loading$$.next({ connector: true });
    this.saveOrUpdateConnector(connectorConfig, this.connectorId)
      .pipe(untilDestroyed(this))
      .subscribe((connector) => {
        if (connector) {
          this.panelRef.close();
        }
        this.loading$$.next({ connector: false });
      });
  }
}
